import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Plus, Calculator, DollarSign, CheckCircle, RotateCcw } from 'lucide-react';
import { theme, cardStyle, inputStyle, btnStyle } from '../theme';

// ═══════════════════════════════════════════════════════════════════
//  DOCTOR COMMISSIONS (Phase 12) — per-procedure commission calc.
// ═══════════════════════════════════════════════════════════════════

const DoctorCommissionsPage = ({ API_BASE }) => {
  const [view, setView] = useState('entries');
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [doctors, setDoctors] = useState([]);
  const [rules, setRules] = useState([]);
  const [entries, setEntries] = useState([]);
  const [docFilter, setDocFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [editingRule, setEditingRule] = useState(null);

  const load = useCallback(async () => {
    try {
      const [d, r, e] = await Promise.all([
        axios.get(`${API_BASE}/doctors`),
        axios.get(`${API_BASE}/commissions/rules`),
        axios.get(`${API_BASE}/commissions/entries`, { params: { month, ...(docFilter ? { doctorId: docFilter } : {}), ...(statusFilter ? { status: statusFilter } : {}) } }),
      ]);
      setDoctors(d.data || []); setRules(r.data || []); setEntries(e.data || []);
    } catch (err) { alert('Load failed: ' + (err.response?.data?.error || err.message)); }
  }, [API_BASE, month, docFilter, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const compute = async () => {
    if (!window.confirm(`Compute commissions for ${month}? Idempotent — existing entries skipped.`)) return;
    try {
      const r = await axios.post(`${API_BASE}/commissions/compute`, { month });
      alert(`Scanned: ${r.data.scanned}, Created: ${r.data.created}, Skipped: ${r.data.skipped}`);
      load();
    } catch (e) { alert('Failed: ' + (e.response?.data?.error || e.message)); }
  };

  const decideEntry = async (id, action, extra = {}) => {
    if (action === 'pay') {
      const ref = prompt('UTR / payout reference (optional):') || '';
      extra.payoutRef = ref;
    }
    try {
      await axios.put(`${API_BASE}/commissions/entries/${id}/${action}`, extra);
      load();
    } catch (e) { alert('Failed: ' + (e.response?.data?.error || e.message)); }
  };

  const saveRule = async () => {
    try {
      if (editingRule._id) await axios.put(`${API_BASE}/commissions/rules/${editingRule._id}`, editingRule);
      else await axios.post(`${API_BASE}/commissions/rules`, editingRule);
      setEditingRule(null); load();
    } catch (e) { alert('Save failed: ' + (e.response?.data?.error || e.message)); }
  };

  const deleteRule = async (id) => {
    if (!window.confirm('Delete rule?')) return;
    try { await axios.delete(`${API_BASE}/commissions/rules/${id}`); load(); }
    catch (e) { alert('Failed: ' + (e.response?.data?.error || e.message)); }
  };

  const totals = entries.reduce((a, e) => {
    a.total += e.commissionAmount || 0;
    a[e.status] = (a[e.status] || 0) + (e.commissionAmount || 0);
    return a;
  }, { total: 0 });

  const monthOptions = () => {
    const out = []; const now = new Date();
    for (let i = 0; i < 6; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const v = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`;
      out.push({ value: v, label: d.toLocaleString('en-IN', { month:'short', year:'numeric' }) });
    }
    return out;
  };

  return (
    <div style={{ padding:20 }}>
      <h2 style={{ color: theme.navy, margin:'0 0 16px' }}>💼 Doctor Commissions</h2>

      <div style={{ ...cardStyle, marginBottom:12, background: theme.primaryLight, border:`1px solid ${theme.primary}` }}>
        <div style={{ fontSize:'0.85rem', color: theme.navy, lineHeight:1.6 }}>
          <b>Flow:</b> Set per-doctor rules (% of treatment bill or flat ₹). Click "Compute for Month" to generate entries from done treatments. Review → Approve → Pay (with UTR). Idempotent per treatment.
        </div>
      </div>

      <div style={{ display:'flex', gap:8, marginBottom:12, alignItems:'center' }}>
        {[['entries','📋 Entries'],['rules','⚙ Rules']].map(([k,l]) => (
          <button key={k} onClick={() => setView(k)} style={{
            padding:'8px 14px', borderRadius:6, cursor:'pointer',
            border:`1px solid ${view===k ? theme.primary : theme.border}`,
            background: view===k ? theme.primary : 'white',
            color: view===k ? 'white' : theme.navy, fontWeight:600,
          }}>{l}</button>
        ))}
        {view === 'rules' && (
          <button onClick={() => setEditingRule({ doctorId:'', treatmentType:'*', percentOfBill:30, fixedAmount:0, minAmount:0, isActive:true })} style={{ ...btnStyle, background: theme.primary, color:'white', display:'flex', gap:6, alignItems:'center' }}>
            <Plus size={14}/> New Rule
          </button>
        )}
      </div>

      {view === 'entries' && (
        <>
          <div style={{ ...cardStyle, marginBottom:12, display:'flex', gap:10, alignItems:'center', flexWrap:'wrap' }}>
            <select value={month} onChange={e => setMonth(e.target.value)} style={{ ...inputStyle, width:180 }}>
              {monthOptions().map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
            </select>
            <select value={docFilter} onChange={e => setDocFilter(e.target.value)} style={{ ...inputStyle, width:200 }}>
              <option value="">All doctors</option>
              {doctors.map(d => <option key={d._id} value={d._id}>{d.name}</option>)}
            </select>
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={{ ...inputStyle, width:150 }}>
              <option value="">All statuses</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="paid">Paid</option>
              <option value="reversed">Reversed</option>
            </select>
            <button onClick={compute} style={{ ...btnStyle, background: theme.secondary, color:'white', display:'flex', gap:6, alignItems:'center' }}>
              <Calculator size={14}/> Compute for Month
            </button>
          </div>

          <div style={{ display:'grid', gridTemplateColumns:'repeat(5,1fr)', gap:10, marginBottom:12 }}>
            <StatCard label="Entries" value={entries.length} color={theme.secondary}/>
            <StatCard label="Pending" value={`₹${(totals.pending || 0).toLocaleString('en-IN')}`} color={theme.warning}/>
            <StatCard label="Approved" value={`₹${(totals.approved || 0).toLocaleString('en-IN')}`} color={theme.info}/>
            <StatCard label="Paid" value={`₹${(totals.paid || 0).toLocaleString('en-IN')}`} color={theme.success}/>
            <StatCard label="Total" value={`₹${totals.total.toLocaleString('en-IN')}`} color={theme.navy}/>
          </div>

          <div style={{ ...cardStyle, padding:0, overflow:'hidden' }}>
            {entries.length === 0 ? <div style={{ padding:40, textAlign:'center', color:'#888' }}>No entries. Set rules and click "Compute for Month".</div>
             : (
              <table style={{ width:'100%', borderCollapse:'collapse' }}>
                <thead><tr style={{ background:'#f9fafb' }}>
                  <th style={th}>Doctor</th><th style={th}>Patient</th><th style={th}>Treatment</th>
                  <th style={th}>Date</th><th style={th}>Base</th><th style={th}>Commission</th>
                  <th style={th}>Status</th><th style={th}>Action</th>
                </tr></thead>
                <tbody>
                  {entries.map(e => (
                    <tr key={e._id} style={{ borderBottom:'1px solid #f1f3f5' }}>
                      <td style={{ ...td, fontWeight:600 }}>{e.doctorName}</td>
                      <td style={td}>{e.patientName}</td>
                      <td style={td}>{e.treatmentName}</td>
                      <td style={td}>{e.doneDate ? new Date(e.doneDate).toLocaleDateString('en-IN') : '—'}</td>
                      <td style={td}>₹{(e.treatmentAmount||0).toLocaleString('en-IN')}</td>
                      <td style={{ ...td, fontWeight:700, color: theme.success }}>₹{(e.commissionAmount||0).toLocaleString('en-IN')}</td>
                      <td style={td}>{e.status}</td>
                      <td style={td}>
                        {e.status === 'pending' && <button onClick={() => decideEntry(e._id, 'approve')} style={{ ...tinyBtn, color: theme.success }}>✓</button>}
                        {e.status === 'approved' && <button onClick={() => decideEntry(e._id, 'pay', { paidBy:'admin' })} style={{ ...tinyBtn, color: theme.success, fontWeight:700 }}>💰 Pay</button>}
                        {['approved','paid'].includes(e.status) && <button onClick={() => decideEntry(e._id, 'reverse')} style={{ ...tinyBtn, color: theme.danger }}><RotateCcw size={12}/></button>}
                        {e.payoutRef && <span style={{ fontSize:'0.7rem', color:'#888', marginLeft:6 }}>UTR: {e.payoutRef}</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
             )}
          </div>
        </>
      )}

      {view === 'rules' && (
        <div style={{ ...cardStyle, padding:0, overflow:'hidden' }}>
          {rules.length === 0 ? <div style={{ padding:40, textAlign:'center', color:'#888' }}>No rules yet</div>
           : (
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead><tr style={{ background:'#f9fafb' }}>
                <th style={th}>Doctor</th><th style={th}>Treatment Type</th><th style={th}>Commission</th>
                <th style={th}>Min Amount</th><th style={th}>Active</th><th style={th}>Action</th>
              </tr></thead>
              <tbody>
                {rules.map(r => (
                  <tr key={r._id} style={{ borderBottom:'1px solid #f1f3f5' }}>
                    <td style={{ ...td, fontWeight:600 }}>{r.doctorName || doctors.find(d => d._id === r.doctorId)?.name || '—'}</td>
                    <td style={td}>{r.treatmentType === '*' ? 'All treatments' : r.treatmentType}</td>
                    <td style={td}>
                      {r.fixedAmount > 0 ? `₹${r.fixedAmount} flat` : `${r.percentOfBill}% of bill`}
                    </td>
                    <td style={td}>{r.minAmount ? `₹${r.minAmount}` : '—'}</td>
                    <td style={td}>{r.isActive ? '✓' : '✗'}</td>
                    <td style={td}>
                      <button onClick={() => setEditingRule(r)} style={tinyBtn}>✏</button>
                      <button onClick={() => deleteRule(r._id)} style={{ ...tinyBtn, color: theme.danger }}>🗑</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
           )}
        </div>
      )}

      {editingRule && (
        <div style={modalBg} onClick={() => setEditingRule(null)}>
          <div style={modalCard} onClick={e => e.stopPropagation()}>
            <h3 style={{ color: theme.navy, margin:'0 0 12px' }}>{editingRule._id ? 'Edit' : 'New'} Rule</h3>
            <label style={lbl}>Doctor</label>
            <select value={editingRule.doctorId} onChange={e => setEditingRule({ ...editingRule, doctorId: e.target.value })} style={{ ...inputStyle, marginBottom:8 }}>
              <option value="">Select…</option>
              {doctors.map(d => <option key={d._id} value={d._id}>{d.name}</option>)}
            </select>
            <label style={lbl}>Treatment Type (exact name or * for all)</label>
            <input value={editingRule.treatmentType} onChange={e => setEditingRule({ ...editingRule, treatmentType: e.target.value })} placeholder="* or exact treatment name" style={{ ...inputStyle, marginBottom:8 }}/>
            <div style={{ display:'flex', gap:8, marginBottom:8 }}>
              <div style={{ flex:1 }}>
                <label style={lbl}>% of bill</label>
                <input type="number" value={editingRule.percentOfBill} onChange={e => setEditingRule({ ...editingRule, percentOfBill: Number(e.target.value) })} style={inputStyle}/>
              </div>
              <div style={{ flex:1 }}>
                <label style={lbl}>OR Fixed ₹ per unit</label>
                <input type="number" value={editingRule.fixedAmount} onChange={e => setEditingRule({ ...editingRule, fixedAmount: Number(e.target.value) })} style={inputStyle}/>
              </div>
            </div>
            <label style={lbl}>Min treatment amount (₹)</label>
            <input type="number" value={editingRule.minAmount} onChange={e => setEditingRule({ ...editingRule, minAmount: Number(e.target.value) })} style={{ ...inputStyle, marginBottom:8 }}/>
            <label style={{ display:'flex', gap:8, alignItems:'center', marginBottom:12 }}>
              <input type="checkbox" checked={editingRule.isActive} onChange={e => setEditingRule({ ...editingRule, isActive: e.target.checked })}/> Active
            </label>
            <div style={{ display:'flex', gap:8 }}>
              <button onClick={() => setEditingRule(null)} style={{ ...btnStyle, flex:1 }}>Cancel</button>
              <button onClick={saveRule} disabled={!editingRule.doctorId} style={{ ...btnStyle, flex:1, background: theme.primary, color:'white', opacity: editingRule.doctorId ? 1 : 0.5 }}>Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const StatCard = ({ label, value, color }) => (
  <div style={{ ...cardStyle, padding:14, borderLeft:`4px solid ${color}` }}>
    <div style={{ fontSize:11, color:'#888', textTransform:'uppercase', letterSpacing:1 }}>{label}</div>
    <div style={{ fontSize:18, fontWeight:700, color: theme.navy, marginTop:4 }}>{value}</div>
  </div>
);

const th = { padding:'10px 12px', textAlign:'left', fontWeight:600, fontSize:'0.8rem', color:'#555', borderBottom:'1px solid #e5e7eb' };
const td = { padding:'10px 12px', fontSize:'0.85rem', color:'#333' };
const tinyBtn = { padding:'4px 10px', fontSize:'0.75rem', border:'1px solid #e5e7eb', background:'white', borderRadius:6, cursor:'pointer', marginRight:4 };
const lbl = { fontSize:12, color:'#555', fontWeight:600, marginBottom:4, display:'block' };
const modalBg = { position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:9999, display:'flex', alignItems:'center', justifyContent:'center', padding:20 };
const modalCard = { background:'white', borderRadius:14, padding:24, maxWidth:500, width:'100%', maxHeight:'90vh', overflowY:'auto' };

export default DoctorCommissionsPage;
